import { ShiftDay } from "../api/shiftDefinitionApi";

/**
 * Vardiya deseni yardımcıları — backend'deki `services/shiftPattern.ts` ile
 * AYNI kuralları uygular (plan 2.24). Buradaki hesaplar yalnızca gösterim
 * içindir; doğrulama backend'de tekrar yapılır.
 *
 * `utils/dateFormats.minutesToTime` kullanılmıyor çünkü o 1440'ı aşan değeri
 * sarmalamıyor — gece vardiyasında bitiş 1440'ı aşar ve "26:00" yazardı.
 */

/** Gün anahtarı biçimi: `2026-08-17`. Backend `shiftPattern` ile aynı. */
export const DAY_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** Haftanın günleri, İK'nın beklediği sırayla (Pazartesi başta). */
export const DAYS_OF_WEEK = [
  { label: "Pazartesi", short: "Pzt", value: 1 },
  { label: "Salı", short: "Sal", value: 2 },
  { label: "Çarşamba", short: "Çar", value: 3 },
  { label: "Perşembe", short: "Per", value: 4 },
  { label: "Cuma", short: "Cum", value: 5 },
  { label: "Cumartesi", short: "Cmt", value: 6 },
  { label: "Pazar", short: "Paz", value: 0 },
];

/** `480` → `"08:00"`. 1440'ı aşan değerler gün içine indirgenir. */
export const minutesToHHMM = (minutes: number): string => {
  const wrapped = ((Math.round(minutes) % 1440) + 1440) % 1440;
  const h = Math.floor(wrapped / 60);
  const m = wrapped % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

/** `"08:00"` → `480`. Bozuk değerde `null`. */
export const hhmmToMinutes = (value: string): number | null => {
  const match = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(value ?? "");
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (h > 23 || m > 59) return null;
  return h * 60 + m;
};

/** Bitiş = başlangıç + çalışma + mola. 1440'ı aşabilir (gece vardiyası). */
export const endMinutesOf = (day: {
  startMinutes: number;
  workMinutes: number;
  breakMinutes?: number;
}): number => day.startMinutes + day.workMinutes + (day.breakMinutes ?? 0);

/** Vardiya gece yarısını aşıyor mu. */
export const spansMidnight = (day: {
  startMinutes: number;
  workMinutes: number;
  breakMinutes?: number;
}): boolean => endMinutesOf(day) > 1440;

/** `"08:00 – 17:00"`, gece vardiyasında `"22:00 – 06:00 (ertesi gün)"`. */
export const dayRangeLabel = (day: ShiftDay): string => {
  const range = `${minutesToHHMM(day.startMinutes)} – ${minutesToHHMM(
    endMinutesOf(day)
  )}`;
  return spansMidnight(day) ? `${range} (ertesi gün)` : range;
};

/**
 * Tablo satırındaki tek satırlık özet: `"Pzt, Sal, Çar · 08:00 – 17:00"`.
 * Günlerin saatleri farklıysa saat kısmı yazılmaz, "değişken" denir.
 */
export const weeklySummary = (days: ShiftDay[] | undefined): string => {
  if (!days || days.length === 0) return "Gün tanımlanmamış";

  const ordered = DAYS_OF_WEEK.filter((d) =>
    days.some((x) => x.dayOfWeek === d.value)
  );
  const dayLabels = ordered.map((d) => d.short).join(", ");

  const first = days[0];
  const allSame = days.every(
    (d) =>
      d.startMinutes === first.startMinutes &&
      d.workMinutes === first.workMinutes &&
      (d.breakMinutes ?? 0) === (first.breakMinutes ?? 0)
  );

  return allSame
    ? `${dayLabels} · ${dayRangeLabel(first)}`
    : `${dayLabels} · saatler güne göre değişiyor`;
};

/** Toplam haftalık fiili çalışma süresi, saat cinsinden. */
export const weeklyWorkHours = (days: ShiftDay[] | undefined): number => {
  const total = (days ?? []).reduce((sum, d) => sum + d.workMinutes, 0);
  return Math.round((total / 60) * 10) / 10;
};

/** `"2026-08-17"` → `"17.08.2026"`. Süresiz atamada bitiş için `"—"`. */
export const formatDayKey = (dayKey: string | null | undefined): string => {
  if (!dayKey) return "—";
  const [y, m, d] = dayKey.split("-");
  return `${d}.${m}.${y}`;
};

/**
 * Gün anahtarına çevirir.
 *
 * ⚠ Mantine 8'de `DatePickerInput` değeri **zaten `"YYYY-MM-DD"` string**
 * (`DateStringValue`), `Date` değil — Mantine 7'den 8'e geçişte değişti.
 * Bu yüzden fonksiyon iki tipi de kabul ediyor: tarih seçicilerden gelen
 * string olduğu gibi geçer, `new Date()` gibi yerlerden gelen `Date`
 * **yerel** güne göre çevrilir (UTC'ye çevirmek gün kaydırırdı).
 */
export const toDayKey = (
  value: Date | string | null | undefined
): string | null => {
  if (!value) return null;
  if (typeof value === "string") {
    // Mantine bazı yollarda "YYYY-MM-DD HH:mm:ss" da verebiliyor.
    const dayPart = value.slice(0, 10);
    return DAY_KEY_PATTERN.test(dayPart) ? dayPart : null;
  }
  const y = value.getFullYear();
  const m = String(value.getMonth() + 1).padStart(2, "0");
  const d = String(value.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

/** Bugünün gün anahtarı. */
export const todayKey = (): string => toDayKey(new Date())!;
